import React, { Suspense, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from '@knitknot/shared/components/ui/console-sidebar';
import { AppShell } from '@knitknot/shared/components/ui/app-shell';
import { AppShellProvider } from '@knitknot/shared/components/ui/app-shell-context';
import { useWorkspaceSwitcher } from '@/hooks/useWorkspaceSwitcher';
import { useWorkspaceStore } from '@/hooks/state';
import { WorkspaceSwitcher } from '@knitknot/shared/components/ui/workspace-switcher';
import { SearchTrigger } from '@knitknot/shared/components/ui/search-trigger';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { CommandPalette } from '@knitknot/shared/components/ui/command-palette';
import { HeaderUserProfile } from '@knitknot/shared/components/ui/header-user-profile';
import { prefetchRoute } from '@/lib/prefetch';
import { ErrorBoundary } from '@knitknot/shared/components/ui/error-boundary';
import { Deferred } from '@knitknot/shared/components/ui/deferred';
import { DetailPageSkeleton } from '@knitknot/shared/components/ui/detail-page-skeleton';
import { useAdminWorkspaceAdoption } from '@/hooks/useAdminWorkspaceAdoption';
import { useConsoleLock } from '@/hooks/api/useOnboarding';
import { OnboardingGate } from './OnboardingGate';
import { OverviewSkeleton } from '@/components/home/OverviewSkeleton';
import { trackIaMetric } from '@/lib/rum';

/**
 * Console layout wrapper that persists across navigation.
 *
 * Layout geometry is driven by two CSS vars on :root, written by ConsoleSidebar:
 *   --app-content-inset   sidebar width — content margin tracks this
 *   --app-topnav-h        top nav height — modals respect this
 *   --app-logo-w          logo column width (static) — top nav sits flush
 */
export const ConsoleLayout: React.FC = () => {
  const { workspaceId } = useWorkspaceStore();
  // The switcher's own label, handed to the shared page chrome so its
  // breadcrumb can drop a crumb that merely repeats it (see AppShellContext).
  const { currentWorkspace, workspaces, isLoading: workspacesLoading } = useWorkspaceSwitcher();
  const location = useLocation();
  const togglePalette = useCommandPalette((s) => s.toggle);
  const prefetched = useRef<string | null>(null);
  const overviewExitTracked = useRef(false);

  // Mounted ONCE, here, and read by both the gate and the sidebar — one query,
  // one cache entry, one answer. See `lib/consoleLock.ts`.
  const { locked, resolving } = useConsoleLock();

  // Superadmin "view as": adopt ?admin_workspace=<id> deep-linked from the
  // internal admin tool.
  useAdminWorkspaceAdoption(workspaces, workspacesLoading);

  useEffect(() => {
    // Never warm a page the workspace cannot open. Prefetching `/sources` or
    // `/prompts` on an un-onboarded workspace fires six requests that all answer
    // `400 No company configured` — which is most of what a broken new workspace
    // logged before the gate existed.
    if (locked || resolving) return;
    const key = `${location.pathname}:${workspaceId}`;
    if (workspaceId && prefetched.current !== key) {
      prefetched.current = key;
      prefetchRoute(location.pathname, workspaceId);
    }
  }, [location.pathname, workspaceId, locked, resolving]);

  useEffect(() => {
    if (location.pathname === '/overview') overviewExitTracked.current = false;
  }, [location.pathname]);

  useEffect(() => {
    const trackNavigation = (surface: string, key: string) => {
      trackIaMetric('ia-navigation', `${surface}:${key}`);
      if (location.pathname === '/overview' && !overviewExitTracked.current) {
        overviewExitTracked.current = true;
        trackIaMetric('ia-overview-first-click', `${surface}:${key}`);
      }
    };
    const onClick = (event: MouseEvent) => {
      const target = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-nav-surface][data-nav-key]')
        : null;
      if (!target) return;
      trackNavigation(target.dataset.navSurface ?? 'unknown', target.dataset.navKey ?? 'unknown');
    };
    const onCustom = (event: Event) => {
      const detail = (event as CustomEvent<{ surface?: string; key?: string }>).detail;
      if (detail?.surface && detail.key) trackNavigation(detail.surface, detail.key);
    };
    document.addEventListener('click', onClick, { capture: true });
    document.addEventListener('knitknot:ia-navigation', onCustom);
    return () => {
      document.removeEventListener('click', onClick, { capture: true });
      document.removeEventListener('knitknot:ia-navigation', onCustom);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (resolving) return;
    trackIaMetric('ia-workspace-state', locked ? 'setup-locked' : 'ready');
  }, [locked, resolving, workspaceId]);

  return (
    <AppShellProvider workspaceCrumbLabel={currentWorkspace?.name}>
      <AppShell
        sidebar={<Sidebar locked={locked} />}
        topBar={
          <>
            <WorkspaceSwitcher />
            <div className="flex-1" />
            <SearchTrigger onOpen={togglePalette} />
            <CommandPalette locked={locked} />
            <div className="flex items-center gap-2 flex-shrink-0">
              <HeaderUserProfile />
            </div>
          </>
        }
      >
        <ErrorBoundary>
          <OnboardingGate
            key={workspaceId ?? 'no-workspace'}
            workspaceId={workspaceId}
            locked={locked}
            resolving={resolving}
          >
            {/* Route-chunk Suspense INSIDE the persistent shell (Rule 59 / perf
              plan §6.2): a lazy page loading never unmounts the sidebar/top
              bar. Fallback is a deferred generic page skeleton — sub-200ms
              chunk loads (the prefetched common case) show nothing, slower
              ones skeleton instead of blanking. */}
            <Suspense
              fallback={location.pathname === '/overview' ? <OverviewSkeleton /> : (
                <Deferred delay={200}>
                  <div className="p-6">
                    <DetailPageSkeleton />
                  </div>
                </Deferred>
              )}
            >
              <Outlet />
            </Suspense>
          </OnboardingGate>
        </ErrorBoundary>
      </AppShell>
    </AppShellProvider>
  );
};
